import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles, createMuiTheme, ThemeProvider } from "@material-ui/core/styles";

import { useInstallation } from "../../../components/InstallationContext";
import { useAppConfig } from "../../../components/AppConfigContext";
import { Loading, LoadingError } from "../../../components/LoadingFork";
import { T, FONT_SANS } from "../constants";
import { useUi } from "../ui";
import {
  offsiteTicketLotDashboardQuery,
  updateOffsiteTicketMutation,
} from "./queries";
import {
  sortLotsForStaff,
  toLotInventoryItem,
  toParkingBookings,
} from "./dashboardData";
import useOffsiteTicketAllocation from "./useOffsiteTicketAllocation";
import LotBar from "./components/LotBar";
import LotInventoryPanel from "./components/LotInventoryPanel";
import BookingFilters from "./components/BookingFilters";
import BookingsTable from "./components/BookingsTable";
import IssueDialog from "./components/IssueDialog";
import ValidateDialog from "./components/ValidateDialog";
import CreateBookingDialog from "./components/CreateBookingDialog";
import TakePaymentDialog from "./components/TakePaymentDialog";
import TopStat from "../components/TopStat";

const offsiteTheme = createMuiTheme({ typography: { fontFamily: FONT_SANS } });

const TICKET_STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "awaiting", label: "Awaiting" },
  { id: "allocated", label: "Allocated" },
  { id: "redeemed", label: "Redeemed" },
  { id: "flagged", label: "Flagged" },
];

const useStyles = makeStyles({
  root: {
    background: T.bg,
    color: T.fg,
    fontFamily: FONT_SANS,
    // Neutralise the console's 20px main gutter so this page owns its padding.
    margin: -20,
    minHeight: "calc(100% + 40px)",
    padding: 24,
    boxSizing: "border-box",
  },
  inner: { maxWidth: 1600, margin: "0 auto" },
  header: {
    borderRadius: 14,
    background: T.primary,
    color: "#fff",
    padding: "20px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 32,
    flexWrap: "wrap",
    marginBottom: 24,
  },
  titleBar: { marginBottom: 16 },
  title: { fontWeight: 700, color: T.fg },
  headerTitle: { margin: 0, fontSize: 26, fontWeight: 300, lineHeight: 1 },
  statRail: { display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" },
  railDivider: { height: 24, width: 1, background: "rgba(255,255,255,0.18)" },
  ruleNote: {
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.surface,
    padding: "10px 14px",
    fontSize: 12,
    color: T.mutedFg,
    marginBottom: 16,
  },
  ruleStrong: { fontWeight: 600, color: T.fg },
  section: { marginTop: 24 },
  sectionHead: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  sectionTitle: { margin: 0, fontSize: 20, fontWeight: 600, color: T.fg },
  sectionTitleRow: { display: "flex", alignItems: "baseline", gap: 8 },
  sectionCount: { fontSize: 13, fontWeight: 600, color: T.mutedFg },
  filters: { marginBottom: 16 },
  footNote: { marginTop: 12, fontSize: 12, color: T.mutedFg },
  loadingWrap: { borderRadius: 8, border: `1px solid ${T.border}`, background: T.elevated, padding: 24 },
});

export default function OffsiteTickets() {
  const s = useStyles();
  const ui = useUi();
  const appConfig = useAppConfig();
  const { installation, facility } = useInstallation();

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [issueOpen, setIssueOpen] = useState(null);
  const [validateOpen, setValidateOpen] = useState(null);
  const [expandedLot, setExpandedLot] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [takePaymentOpen, setTakePaymentOpen] = useState(null);
  const [showAllLots, setShowAllLots] = useState(false);

  const { data, loading, error, refetch } = useQuery(offsiteTicketLotDashboardQuery, {
    fetchPolicy: "no-cache",
  });
  const { allocateTicket: allocateOffsiteTicket, allocating: allocatingTicket } =
    useOffsiteTicketAllocation({ installationId: installation?.id });
  const [updateOffsiteTicket, { loading: updatingTicket }] = useMutation(
    updateOffsiteTicketMutation,
  );

  const dashboard = data?.offsiteTicketLotDashboard;
  const summary = dashboard?.summary || {};
  const lots = useMemo(
    () => (dashboard?.lots || []).map(toLotInventoryItem).sort(sortLotsForStaff),
    [dashboard],
  );

  const parkingBookings = useMemo(() => toParkingBookings(lots), [lots]);

  const awaiting = parkingBookings.filter((b) => b.status === "awaiting");
  const offsiteLots = useMemo(() => lots.filter((l) => l.type === "OFFSITE"), [lots]);
  const visibleLots = showAllLots ? lots : lots.slice(0, 3);
  const shouldShowLotsToggle = lots.length > 3;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return parkingBookings
      .filter((b) => (statusFilter === "all" ? true : b.status === statusFilter))
      .filter((b) =>
        !q
          ? true
          : [
              b.ref,
              b.guestName,
              b.email,
              b.phone,
              b.plate,
              b.voucherId,
              b.status,
              b.lotName,
              b.booking?.id,
            ].some((field) => String(field || "").toLowerCase().includes(q)),
      );
  }, [parkingBookings, query, statusFilter]);

  const lotById = (id) => (id ? lots.find((l) => l.id === id) : undefined);

  const allocateTicket = async (input) => {
    const lot = lotById(input.lotId);
    const booking = input.booking || parkingBookings.find((item) => item.ref === input.bookingRef);
    const ticket = await allocateOffsiteTicket({ ...input, booking, lot });

    if (ticket) setIssueOpen(null);

    return ticket;
  };

  const createWalkIn = (input) => {
    const lot = lotById(input.lotId);
    if (!lot || lot.available <= 0) return;
    setIssueOpen(null);
  };

  const updateTicketStatus = async (booking, status, notes) => {
    const ticketId = booking?.offsiteTicket?.id;
    const cleanNotes = notes?.trim();

    if (!ticketId) {
      window.alert("Offsite ticket is required to update status.");
      return;
    }

    try {
      await updateOffsiteTicket({
        variables: {
          input: {
            id: ticketId,
            status,
            ...(cleanNotes ? { notes: cleanNotes } : {}),
          },
        },
        refetchQueries: ["offsiteTicketLotDashboard"],
        awaitRefetchQueries: true,
      });
      setValidateOpen(null);
    } catch (err) {
      window.alert(err.message || "Failed to update offsite ticket.");
    }
  };

  const markRedeemed = (booking, notes) => {
    updateTicketStatus(booking, "REDEEMED", notes);
  };

  const flagBooking = (booking, notes) => {
    updateTicketStatus(booking, "FLAGGED", notes);
  };

  const createOnBehalf = () => {
    setCreateOpen(false);
  };

  const openBooking = (booking) => {
    const bookingId = booking?.booking?.id || booking?.id || booking?.ref;

    if (!installation?.brandingKey || !bookingId) return;

    const tab = window.open(`/${installation.brandingKey}/bookings/${bookingId}`, "_blank", "noopener,noreferrer");

    if (tab) {
      tab.opener = null;
    }
  };

  const refreshAfterPayment = useCallback(() => {
    refetch();
  }, [refetch]);

  // Ticket allocation is limited to off-site lots; the dashboard still shows all lots.
  const suggestedLotId =
    offsiteLots.find((l) => l.available > 0)?.id ??
    offsiteLots[0]?.id ??
    "";

  return (
    <ThemeProvider theme={offsiteTheme}>
      <Box className={s.root}>
        <div className={s.inner}>
          <div className={s.titleBar}>
            <Typography variant="h5" component="span" className={s.title}>Concierge - Offsite Tickets</Typography>
          </div>
          <div className={s.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img
                src="/DRIVO_Logo.png"
                alt="DRIVO"
                style={{ height: 20, width: "auto", display: "block", filter: "brightness(0) invert(1)" }}
              />
              <span style={{ height: 24, width: 1, background: "rgba(255,255,255,0.25)" }} />
              <div>
                <h1 className={s.headerTitle}>Front Desk Console - {installation?.name || "Hotel"}</h1>
              </div>
            </div>
            <div className={s.statRail}>
              <TopStat label="Tickets left" value={summary.ticketsLeft || 0} />
              <span className={s.railDivider} />
              <TopStat label="Pre-booked" value={parkingBookings.length} />
              <TopStat label="Allocated" value={summary.allocated || 0} />
              <TopStat label="Flags" value={summary.flags || 0} />
            </div>
          </div>

          <div className={s.ruleNote}>
            Allocation rule: <span className={s.ruleStrong}>Hotel Car Park fills first</span>,
            then overflow allocated to the public car park.
          </div>

          {loading ? (
            <div className={s.loadingWrap}>
              <Loading />
            </div>
          ) : error ? (
            <div className={s.loadingWrap}>
              <LoadingError error={error} />
            </div>
          ) : (
            <>
              <LotBar
                lots={visibleLots}
                expandedLot={expandedLot}
                onToggle={setExpandedLot}
                toggleLabel={showAllLots ? "Show less" : "Show more"}
                onToggleMore={shouldShowLotsToggle ? () => setShowAllLots((value) => !value) : null}
              />
              {expandedLot && (
                <LotInventoryPanel
                  lot={lots.find((l) => l.id === expandedLot)}
              bookings={parkingBookings.filter((booking) => booking.lotId === expandedLot)}
              timezone={installation?.timeZoneName}
              onClose={() => setExpandedLot(null)}
              onOpenBooking={openBooking}
              onTakePayment={setTakePaymentOpen}
              onAssign={(b) => setIssueOpen({ bookingRef: b.ref, booking: b })}
              onValidate={(b) => setValidateOpen(b)}
                />
              )}
            </>
          )}

          <div className={s.section}>
            <div className={s.sectionHead}>
              <div className={s.sectionTitleRow}>
                <h2 className={s.sectionTitle}>Pre-booked parking</h2>
                <span className={s.sectionCount}>{parkingBookings.length} bookings</span>
              </div>
              <button onClick={() => setCreateOpen(true)} className={ui.primaryBtn}>+ Create booking</button>
            </div>

            <div className={s.filters}>
              <BookingFilters
                query={query}
                onQuery={setQuery}
                statusFilter={statusFilter}
                onStatusFilter={setStatusFilter}
                awaitingCount={awaiting.length}
                pills={TICKET_STATUS_FILTERS}
                placeholder="Search by booking, ticket, plate, guest, email or lot..."
              />
            </div>

            <BookingsTable
              bookings={filtered}
              timezone={installation?.timeZoneName}
              onOpenBooking={openBooking}
              onTakePayment={setTakePaymentOpen}
              onAssign={(b) => setIssueOpen({ bookingRef: b.ref, booking: b })}
              onValidate={(b) => setValidateOpen(b)}
            />

            <p className={s.footNote}>
              Payment must be taken before a ticket can be allocated, except on
              guest cards the hotel invoices - those allocate straight away. Cars
              without a matching booking must be created on-behalf first.
            </p>
          </div>
        </div>

        {issueOpen && (
          <IssueDialog
            lots={lots}
            /* `paid` is the desk's answer, not Stripe's: a chargeback booking
               reads as settled here, which is what lets its ticket be
               allocated at all. See dashboardData.toLotInventoryItem. */
            awaiting={awaiting.filter((b) => b.paid)}
            initial={issueOpen}
            defaultLotId={suggestedLotId}
            installationId={installation?.id}
            allocating={allocatingTicket}
            onCancel={() => setIssueOpen(null)}
            onAllocate={allocateTicket}
            onCreateWalkIn={createWalkIn}
          />
        )}

        {validateOpen && (
          <ValidateDialog
            booking={validateOpen}
            installation={installation}
            lot={lotById(validateOpen.lotId)}
            loading={updatingTicket}
            onClose={() => setValidateOpen(null)}
            onRedeem={(notes) => markRedeemed(validateOpen, notes)}
            onFlag={(notes) => flagBooking(validateOpen, notes)}
          />
        )}

        {createOpen && (
          <CreateBookingDialog
            installation={installation}
            facility={facility}
            lots={lots}
            onCancel={() => setCreateOpen(false)}
            onCreate={createOnBehalf}
          />
        )}

        {takePaymentOpen && (
          <TakePaymentDialog
            appConfig={appConfig}
            booking={takePaymentOpen}
            country={facility?.city?.country}
            installation={installation}
            onClose={() => setTakePaymentOpen(null)}
            onPaid={refreshAfterPayment}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}
